import React from "react"
import { Chip } from "@mui/material"
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined"

export interface TimeSummaryTagProps {
  startTimes: Date[] | number[]
  duration?: number
}

/**
 * Formats and displays the selected meeting slot summary from the raw time data.
 */
const TimeSummaryTag: React.FC<TimeSummaryTagProps> = ({
  startTimes,
  duration,
}: TimeSummaryTagProps) => {
  const summaryTagBaseSx = {
    px: 0.5,
    py: 2,
    fontSize: "1.15rem",
    fontWeight: 600,
    borderRadius: 999,
  }

  const getSelectedSlotCount = (slots: Date[]) => {
    return slots.length
  }

  const label = React.useMemo(() => {
    if (!startTimes.length) {
      return ""
    }

    const normalizedStartTimes = startTimes.map((value) => value instanceof Date ? value : new Date(value))

    const selectedSlotCount = getSelectedSlotCount(normalizedStartTimes)

    const getTimezoneLabel = (value: Date) => {
      return new Intl.DateTimeFormat("en-AU", {
        timeZoneName: "short",
      }).formatToParts(value).find((part) => part.type === "timeZoneName")?.value || "local"
    }

    const formatTimeLabel = (value: Date) => {
      const hours24 = value.getHours()
      const hour12 = hours24 % 12 || 12
      const minutes = String(value.getMinutes()).padStart(2, "0")
      const meridiem = hours24 >= 12 ? "pm" : "am"

      return `${hour12}.${minutes}${meridiem}`
    }

    const formatDateLabel = (value: Date, withTime = true) => {
      const today = new Date()
      const tomorrow = new Date(today)
      tomorrow.setDate(today.getDate() + 1)

      const isToday =
        value.getFullYear() === today.getFullYear() &&
        value.getMonth() === today.getMonth() &&
        value.getDate() === today.getDate()

      const isTomorrow =
        value.getFullYear() === tomorrow.getFullYear() &&
        value.getMonth() === tomorrow.getMonth() &&
        value.getDate() === tomorrow.getDate()

      if (isToday) {
        if (!withTime) return "Today"
        return `Today at ${formatTimeLabel(value)} (${getTimezoneLabel(value)})`
      }

      if (isTomorrow) {
        if (!withTime) return "Tomorrow"
        return `Tomorrow at ${formatTimeLabel(value)} (${getTimezoneLabel(value)})`
      }

      const weekday = value.toLocaleString("en-AU", { weekday: "long" })
      const month = value.toLocaleString("en-AU", { month: "long" })
      const day = value.getDate()

      if (!withTime) {
        return `${weekday} ${month} ${day}`
      }

      return `${weekday} ${month} ${day} at ${formatTimeLabel(value)} (${getTimezoneLabel(value)})`
    }

    if (selectedSlotCount > 1) {
      const firstStartTime = normalizedStartTimes[0]
      return `${formatDateLabel(firstStartTime, false)} - ${selectedSlotCount} timeslots`
    }

    const [startTime] = normalizedStartTimes

    if (duration) {
      const endTime = new Date(startTime.getTime() + duration * 60000)
      return `${formatDateLabel(startTime, false)} at ${formatTimeLabel(startTime)} to ${formatTimeLabel(endTime)} (${getTimezoneLabel(startTime)})`
    }

    return formatDateLabel(startTime)
  }, [duration, startTimes])

  if (!label) {
    return null
  }

  return (
    <Chip
      icon={<CalendarMonthOutlinedIcon fontSize="small" />}
      label={label}
      size="small"
      sx={{
        ...summaryTagBaseSx,
        backgroundColor: "#dbeafe",
        color: "#0077e6",
        ".MuiChip-icon": {
          color: "#0077e6",
        },
      }}
    />
  )
}

export default TimeSummaryTag
